"use client";

import Link from "next/link";
import Image from "next/image";
import { useLanguage } from "@/context/LanguageContext";

export function Footer() {
  const { language } = useLanguage();

  const labels = {
    en: {
      tagline: "GaneshaIT is here for your company's IT solutions",
      services: "Services",
      resources: "Resources",
      company: "Company",
      solutions: "Solutions",
      research: "Research",
      blog: "Blogs",
      guides: "Guides & Tutorials",
      terms: "Terms of Service",
      contact: "Contact Us",
      careers: "Careers",
      team: "Team",
      privacy: "Privacy Policy",
      copyright: "© 2026 MarkAny GaneshaIT.",
    },
    id: {
      tagline: "GaneshaIT hadir untuk solusi TI perusahaan Anda",
      services: "Layanan",
      resources: "Sumber Daya",
      company: "Perusahaan",
      solutions: "Solusi",
      research: "Riset",
      blog: "Blog",
      guides: "Panduan & Tutorial",
      terms: "Ketentuan Layanan",
      contact: "Hubungi Kami",
      careers: "Karier",
      team: "Tim",
      privacy: "Kebijakan Privasi",
      copyright: "© 2026 MarkAny GaneshaIT.",
    },
  };

  const t = labels[language];

  return (
    <footer
      className="text-white mt-auto min-h-75 w-full"
      style={{
        background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)",
      }}
    >
      {/* Upper Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-16 py-8 md:py-12">
        {/* Mobile Layout */}
        <div className="md:hidden flex flex-col items-center text-center space-y-8">
          {/* Logo + Tagline - Centered */}
          <div>
            <div className="flex justify-center mb-3">
              <Image
                src="/footer/markany-ganeshait.svg"
                alt="MarkAny"
                width={120}
                height={32}
                className="h-auto w-28"
                priority
              />
            </div>
            <p className="text-xs text-white leading-relaxed">
              {t.tagline}
            </p>
          </div>

          {/* Resources & Company - Two Columns */}
          <div className="grid grid-cols-2 gap-x-12 w-full max-w-xs">
            {/* Resources */}
            <div className="text-center">
              <h3 className="font-extrabold text-xs mb-2 text-white">{t.resources}</h3>
              <ul className="space-y-1.5 text-xs">
                <li>
                  <Link href="/blogs" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                    {t.blog}
                  </Link>
                </li>
                <li>
                  <Link href="/guides" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                    {t.guides}
                  </Link>
                </li>
                {/* <li>
                  <Link href="/terms-of-service" className="text-gray-300 hover:text-gray-400 transition-colors">
                    Terms of Service
                  </Link>
                </li> */}
              </ul>
            </div>

            {/* Company */}
            <div className="text-center">
              <h3 className="font-extrabold text-xs mb-2 text-white">{t.company}</h3>
              <ul className="space-y-1.5 text-xs">
                <li>
                  <Link href="/contact" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                    {t.contact}
                  </Link>
                </li>
                <li>
                  <Link href="/careers" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                    {t.careers}
                  </Link>
                </li>
                <li>
                  <Link href="/team" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                    {t.team}
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Services - Centered */}
          <div>
            <h3 className="font-extrabold text-xs mb-2 text-white">{t.services}</h3>
            <ul className="space-y-1.5 text-xs">
              <li>
                <Link href="/solutions" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                  {t.solutions}
                </Link>
              </li>
              <li>
                <Link href="/research" className="text-gray-300 hover:text-gray-400 transition-colors block py-1 px-2 -mx-2 rounded">
                  {t.research}
                </Link> 
              </li>
            </ul>
          </div>
        </div>

        {/* Desktop Layout - 2 column layout */}
        <div className="hidden md:grid grid-cols-[auto_1fr] items-start gap-12 lg:gap-16">
          {/* Left: Logos + Tagline */}
          <div className="max-w-sm">
            <div className="flex items-center gap-3">
              <Image
                src="/footer/markany-ganeshait.svg"
                alt="MarkAny"
                width={120}
                height={32}
                className="h-auto w-auto"
                priority
              />
            </div>

            <p className="mt-3 text-sm text-white leading-relaxed">
              {t.tagline}
            </p>
          </div>

          {/* Right: Link Columns (pushed to the right + closer together) */}
          <div className="flex justify-end pl-12 lg:pl-20">
            <div className="grid grid-cols-3 gap-x-12 gap-y-8">
              {/* Services */}
              <div>
                <h3 className="font-extrabold text-sm mb-3 text-white">{t.services}</h3>
                <ul className="space-y-2 text-sm ">
                  <li>
                    <Link href="/solutions" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.solutions}
                    </Link>
                  </li>
                  <li>
                    <Link href="/research" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.research}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h3 className="font-extrabold text-sm mb-3 text-white">{t.resources}</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link href="/blogs" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.blog}
                    </Link>
                  </li>
                  <li>
                    <Link href="/guides" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.guides}
                    </Link>
                  </li>
                  <li>
                    <Link href="/terms-of-service" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.terms}
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h3 className="font-extrabold text-sm mb-3 text-white">{t.company}</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link href="/contact" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.contact}
                    </Link>
                  </li>
                  <li>
                    <Link href="/careers" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.careers}
                    </Link>
                  </li>
                  <li>
                    <Link href="/team" className="text-gray-300 hover:text-gray-400 transition-colors">
                      {t.team}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lower Section */}
      <div className="border-t border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-gray-300">

            {/* Left */}
            <Link
              href="/terms-of-service"
              className="hover:text-white transition-colors"
            >
              {t.terms}
            </Link>

            {/* Center */}
            <p className="text-white text-center">
              {t.copyright}
            </p>

            {/* Right */}
            <Link
              href="/privacy-policy"
              className="hover:text-white transition-colors"
            >
              {t.privacy}
            </Link>

          </div>
        </div>
      </div>

    </footer>
  );
}